import { Icons } from '@cockpitai/components/atoms'
import { Typography } from '@cockpitai/components/atoms/Typography'
import { SectionList } from '@cockpitai/components/organisms/Header/components/HeaderChatActions/components/HistoryChat/SectionList'
import {
  TSection,
  useHistoryChat,
} from '@cockpitai/components/organisms/Header/components/HeaderChatActions/components/HistoryChat/useHistoryChat'
import { urlCipher } from '@cockpitai/utils/urlCipher'
import { AnimatePresence, motion } from 'framer-motion'
import { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { useSideBar } from '../../SideBarContext'
import { MenuItem } from '../MenuItem'

type ToggleableMenuProps = {
  title: string
  icon: ReactNode
  menuItems: MenuItem[] | TSection[]
  isOpen: boolean
  toggleIsOpen: () => void
  isFavList?: boolean
  isRecentChatList?: boolean
  resetChat: () => void
  notStartBreadcrumb?: boolean
  onItemClick?: (item: { name: string; to: string; id?: string }) => void
}

export function ToggleableMenu({
  title,
  icon,
  menuItems,
  isOpen,
  toggleIsOpen,
  resetChat,
  isFavList = false,
  isRecentChatList = false,
  notStartBreadcrumb = false,
}: ToggleableMenuProps) {
  const {
    sections,
    handleSelectMessage,
    isLoading: isLoadingRecentChatItems,
  } = useHistoryChat()

  const { setSidebarIsOpen } = useSideBar()

  return (
    <li className="flex flex-1 flex-col space-y-2 overflow-hidden">
      <button className="toggle-list-button" onClick={toggleIsOpen}>
        <div>
          {icon}
          <Typography className="whitespace-nowrap text-white" weight="light">
            {title}
          </Typography>
        </div>

        <Icons.ChevronDown
          width={14}
          height={12}
          className={twMerge(
            'transition-transform duration-200',
            isOpen ? '-rotate-90 text-white' : 'rotate-0 text-gray-400',
          )}
        />
      </button>
      <ul
        className={twMerge(
          'inner-list',
          isOpen ? 'h-auto pb-1 opacity-100' : '!m-0 h-0 opacity-0',
          isRecentChatList ? 'inner-recent-list' : '',
        )}>
        {isRecentChatList && isLoadingRecentChatItems && (
          <li className="loading-list w-full px-4">
            <p className="animate-pulse font-outfit text-white">
              Carregando...
            </p>
          </li>
        )}
        {isRecentChatList && !isLoadingRecentChatItems && (
          <li className={twMerge(isRecentChatList && 'recent-chat-container')}>
            {sections.length > 0 ? (
              sections.map((section) => (
                <SectionList
                  key={section.title}
                  title={section.title}
                  items={section.items}
                  onClick={(historyChatId) => {
                    handleSelectMessage(historyChatId)

                    setSidebarIsOpen(false)
                  }}
                />
              ))
            ) : (
              <div className="no-message-yet">
                <Typography
                  type="bodyOne"
                  weight="medium"
                  className="px-2 text-white/50">
                  Nenhuma conversa encontrada
                </Typography>
              </div>
            )}
          </li>
        )}

        <AnimatePresence initial={false}>
          {!isRecentChatList &&
            menuItems.map((item) => {
              if ('name' in item) {
                return (
                  isOpen && (
                    <motion.li
                      key={'menu-item-' + (item?.to ? urlCipher(item.to) : '')}
                      initial={{ opacity: 0, x: -50 }}
                      animate={{ opacity: 1, x: 0 }}>
                      <MenuItem
                        item={item}
                        isFav={isFavList}
                        resetChat={resetChat}
                        notStartBreadcrumb={notStartBreadcrumb}
                      />
                    </motion.li>
                  )
                )
              }

              return <></>
            })}
        </AnimatePresence>
      </ul>
    </li>
  )
}
